import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, PackageSearch, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, PLATFORMS } from "@/lib/api";
import { ProductGrid, ProductSkeleton, EmptyState } from "@/components/vaulted";
import { CategoryIcon } from "@/components/CategoryIcon";
import { cn } from "@/lib/utils";

const SORTS = [["newest", "Newest"], ["skins", "Most Skins"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["rating", "Top Rated"], ["popular", "Most Popular"]];

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [minP, setMinP] = useState(params.get("min") || "");
  const [maxP, setMaxP] = useState(params.get("max") || "");
  const category = params.get("category") || "";
  const sort = params.get("sort") || "newest";
  const featured = params.get("featured") === "1";
  const inStock = params.get("stock") === "1";
  const page = Number(params.get("page") || 1);
  const platform = params.get("platform") || "";
  const ec = params.get("ec") === "1";
  const fa = params.get("fa") === "1";
  const minSkins = params.get("skins") || "";

  const set = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setParams(n); };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);
  useEffect(() => {
    setData(null);
    api.get("/products", { params: { q: params.get("q") || undefined, category: category || undefined, sort, featured: featured || undefined,
      in_stock: inStock || undefined, platform: platform || undefined, email_changeable: ec || undefined, full_access: fa || undefined,
      min_skins: minSkins || undefined, min_price: params.get("min") || undefined, max_price: params.get("max") || undefined, page, limit: 24 } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 0 }));
  }, [params]); // eslint-disable-line

  const applyPrice = (e) => { e.preventDefault(); const n = new URLSearchParams(params); minP ? n.set("min", minP) : n.delete("min"); maxP ? n.set("max", maxP) : n.delete("max"); n.delete("page"); setParams(n); };
  const activeCat = cats.find((c) => c.slug === category);

  return (
    <div className="container-v py-8" data-testid="marketplace-page">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="v-eyebrow">Marketplace</p>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-white">{activeCat ? activeCat.name : "All Fortnite Accounts"}</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="marketplace-result-count">{data ? `${data.total} ${data.total === 1 ? "result" : "results"}` : "Loading…"}</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }} className="relative w-full md:w-96">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input data-testid="marketplace-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts or skins (e.g. Renegade Raider)…" className="v-input pl-9 pr-20" />
          <button data-testid="marketplace-search-submit" className="absolute right-1 top-1 v-btn h-8 px-3 bg-[#27272A] hover:bg-zinc-700 text-xs text-white">Search</button>
        </form>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto scrollbar-thin pb-1" data-testid="category-filters">
        <button onClick={() => set("category", "")} data-testid="category-filter-all"
          className={cn("shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors", !category ? "border-violet-500 bg-violet-500/15 text-white" : "border-[#27272A] text-zinc-400 hover:text-white hover:border-zinc-600")}>All</button>
        {cats.map((c) => (
          <button key={c.id} onClick={() => set("category", c.slug)} data-testid={`category-filter-${c.slug}`}
            className={cn("shrink-0 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors", category === c.slug ? "border-violet-500 bg-violet-500/15 text-white" : "border-[#27272A] text-zinc-400 hover:text-white hover:border-zinc-600")}>
            <CategoryIcon name={c.icon} className="h-3.5 w-3.5" />{c.name}
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-[#27272A] bg-[#111116] p-2.5">
        <SlidersHorizontal className="ml-1 h-4 w-4 text-zinc-500" />
        <form onSubmit={applyPrice} className="flex items-center gap-1.5">
          <input data-testid="price-min-input" type="number" min="0" value={minP} onChange={(e) => setMinP(e.target.value)} placeholder="Min $" className="v-input h-8 w-20 text-xs" />
          <span className="text-zinc-600">–</span>
          <input data-testid="price-max-input" type="number" min="0" value={maxP} onChange={(e) => setMaxP(e.target.value)} placeholder="Max $" className="v-input h-8 w-20 text-xs" />
          <button data-testid="price-apply-button" className="v-btn-ghost v-btn-sm">Apply</button>
        </form>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input data-testid="filter-featured" type="checkbox" checked={featured} onChange={(e) => set("featured", e.target.checked ? "1" : "")} className="accent-violet-500" /> Featured
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input data-testid="filter-in-stock" type="checkbox" checked={inStock} onChange={(e) => set("stock", e.target.checked ? "1" : "")} className="accent-violet-500" /> In stock only
        </label>
        <select data-testid="filter-platform" value={platform} onChange={(e) => set("platform", e.target.value)} className="v-input h-8 w-32 text-xs">
          <option value="">All platforms</option>{PLATFORMS.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select data-testid="filter-min-skins" value={minSkins} onChange={(e) => set("skins", e.target.value)} className="v-input h-8 w-28 text-xs">
          <option value="">Any skins</option>{[10, 50, 100, 200, 300].map((n) => <option key={n} value={n}>{n}+ skins</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input data-testid="filter-email-changeable" type="checkbox" checked={ec} onChange={(e) => set("ec", e.target.checked ? "1" : "")} className="accent-violet-500" /> Email changeable
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input data-testid="filter-full-access" type="checkbox" checked={fa} onChange={(e) => set("fa", e.target.checked ? "1" : "")} className="accent-violet-500" /> Full access
        </label>
        <div className="ml-auto w-48">
          <Select value={sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="h-8 text-xs bg-[#0d0d10] border-[#27272A]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111116] border-[#27272A]">
              {SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-option-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {data === null ? <ProductSkeleton n={8} /> : data.items.length ? (
        <>
          <ProductGrid items={data.items} testId="marketplace-grid" />
          {data.pages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button disabled={page <= 1} onClick={() => set("page", String(page - 1))} className="v-btn-ghost v-btn-sm" data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-zinc-400">Page {page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => set("page", String(page + 1))} className="v-btn-ghost v-btn-sm" data-testid="page-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={PackageSearch} title="No products match your filters" body="Try a different search or clear some filters." testId="marketplace-empty"
          action={<Link to="/marketplace" className="v-btn-ghost v-btn-sm">Clear filters</Link>} />
      )}
    </div>
  );
}
